import React, { useRef, useState } from 'react';
import { useIntl } from 'umi';
import { notification } from 'tea-component';
import classnames from 'classnames';
import { checkUploadFiles } from '@home/utils/file';

import './index.scss';

import FileUploadImage from '@/pages/aigc/assets/svg/file-upload.svg';

// 开启批量上传时，并发上传文件数量限制
const MAX = 6;

// 允许上传的文件格式
const ACCEPT_MIMETYPES = [
  // mp4格式
  'video/mp4',
  // mov格式
  'video/quicktime',
];

interface IUploaderProps {
  multiple?: boolean;
  max?: number;
  accept?: string[];
  onSelect: (files: File[], e: React.ChangeEvent<HTMLElement> | React.DragEvent<HTMLInputElement>) => void;
  footer?: React.ReactElement;
  placeholder?: React.ReactElement;
  placeholderWhenDragging?: React.ReactElement;
  className?: string;
  style?: React.CSSProperties;
}

const transferFiles2FileArray = (files: FileList | null): File[] => {
  if (!files) return [];
  return Array.prototype.slice.call(files, 0);
};

const UploaderPlaceholder = () => {
  const { formatMessage } = useIntl();

  return (
    <div className="dba-uploader__placeholder">
      <img src={FileUploadImage} className="dba-uploader__placeholder__icon" alt="" />
      <div className="dba-uploader__placeholder__info-list">
        <div className="dba-uploader__placeholder__info-item">
          {formatMessage({ id: 'dba_motion_capture_uploader_placeholder_text1' })}
        </div>
        <div className="dba-uploader__placeholder__info-item">
          {formatMessage({ id: 'dba_motion_capture_uploader_placeholder_text2' })}
        </div>
      </div>
    </div>
  );
};

const UploaderPlaceholderWhenDragging = () => {
  const { formatMessage } = useIntl();

  return (
    <div className="dba-uploader__placeholder">
      <img src={FileUploadImage} className="dba-uploader__placeholder__icon" alt="" />
      <div className="dba-uploader__placeholder__info-list">
        <div className="dba-uploader__placeholder__info-item">
          {formatMessage({ id: 'dba_motion_capture_release_the_mouse' })}
        </div>
      </div>
    </div>
  );
};

const UploaderFooter = () => {
  const { formatMessage } = useIntl();

  return <div className="dba-uploader__footer">{formatMessage({ id: 'dba_motion_capture_uploader_footer_text' })}</div>;
};

const Uploader: React.FC<IUploaderProps> = (props) => {
  const {
    multiple = false,
    max = MAX,
    accept = ACCEPT_MIMETYPES,
    onSelect,
    placeholder = <UploaderPlaceholder />,
    placeholderWhenDragging = <UploaderPlaceholderWhenDragging />,
    footer = <UploaderFooter />,
    style,
    className,
  } = props;

  const { formatMessage } = useIntl();

  const fileInput = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  const getUploadErrMsg = (
    type: 1 | 2 | (number & {}),
    options: {
      multiple: boolean;
      max: number;
      mimeType: string;
    },
  ): string => {
    const { multiple, max, mimeType } = options || {};
    const countLimitTipsLocaleKey = multiple
      ? 'dba_motion_capture_upload_files_multiple_count_tips'
      : 'dba_motion_capture_upload_files_single_count_tips';
    const countLimitTipsMax = multiple ? max : 1;
    const errMsgs: {
      [key: string]: string;
    } = {
      // 文件数量超过上限
      1: formatMessage({ id: countLimitTipsLocaleKey }, { max: countLimitTipsMax }),
      // 存在文件mimeType不匹配
      2: formatMessage({ id: 'dba_motion_capture_upload_files_type_tips' }, { mimeType }),
    };
    return errMsgs[type] || formatMessage({ id: 'dba_motion_capture_upload_files_unknow_error_tips' });
  };

  const validateUploadFilesWithErrorNotice = (files: FileList | null) => {
    const { isValid, ...restValidationInfos } = checkUploadFiles(files, {
      multiple,
      max,
      accept,
    });
    if (!isValid) {
      const { type = 999, data } = restValidationInfos || {};
      const { mimeTypes } = data || {};
      const mimeTypesText = mimeTypes?.join(', ') || '';
      const errMsg = getUploadErrMsg(type, { multiple, max, mimeType: mimeTypesText });

      notification.error({
        description: errMsg,
        // className: 'filter-lock-alert',
        duration: 3000,
      });
    }
    return isValid;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
  };
  const handleDragLeave = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e?.preventDefault();
    const originalFiles = e?.dataTransfer?.files;
    if (!originalFiles?.length) {
      setIsDragging(false);
      return;
    }
    if (!validateUploadFilesWithErrorNotice(originalFiles)) {
      setIsDragging(false);
      return;
    }
    onSelect(transferFiles2FileArray(originalFiles), e);
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const originalFiles = e?.target?.files;
    if (!validateUploadFilesWithErrorNotice(originalFiles)) return;
    onSelect(transferFiles2FileArray(e?.target?.files), e);
    fileInput.current!.value = '';
  };

  return (
    <div style={style} className={classnames('dba-uploader', className, { highlight: isDragging })}>
      <div className="dba-uploader__content">
        <input
          ref={fileInput}
          type="file"
          className="dba-uploader__input"
          multiple={multiple}
          accept={accept?.join(',')}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onChange={handleFileInputChange}
        />
        {!isDragging && React.isValidElement(placeholder) && placeholder}
        {isDragging && React.isValidElement(placeholderWhenDragging) && placeholderWhenDragging}
      </div>
      {React.isValidElement(footer) && footer}
    </div>
  );
};

export default Uploader;
